import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Wand2, ImagePlus, Sparkles, Camera } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/* Hidden owner-only shortcut: /owner/quick-test
 *
 * Provisions a fresh unlocked test project tagged `is_quick_test: true`
 * and navigates to its workspace. Two modes:
 *   - Sample photo (synthetic Pillow drawing): instant, good for UI demos
 *     but NOT representative of real AI render quality.
 *   - Upload your own photo: critical for Phase 2 Day 2 AI validation —
 *     drop a real bedroom / kitchen / living / bathroom photo and walk
 *     through the multi-surface scheme builder to log prompt feedback.
 *
 * Hard-gated by `is_owner`. */
export default function OwnerQuickTest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("choose"); // choose | provisioning | error
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("interior");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const isOwner = !!(user && user !== false && user.is_owner);

  useEffect(() => {
    // Reset to choose state when auth resolves
    if (user !== null && isOwner && status === "idle") setStatus("choose");
  }, [user, isOwner, status]);

  const provisionSample = async () => {
    setStatus("provisioning");
    try {
      const form = new FormData();
      form.append("project_scope", scope);
      const { data } = await api.post("/owner/quick-test-project", form);
      nav(`/visualiser/p/${data.project_id}`, { replace: true });
    } catch (e) {
      setStatus("error");
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "Could not provision");
    }
  };

  const provisionWithUpload = async (file) => {
    if (!file) return;
    setStatus("provisioning");
    try {
      const form = new FormData();
      form.append("photo", file);
      form.append("project_scope", scope);
      form.append("label", file.name.slice(0, 60));
      const { data } = await api.post("/owner/quick-test-project", form);
      nav(`/visualiser/p/${data.project_id}`, { replace: true });
    } catch (e) {
      setStatus("error");
      setError(formatApiError(e?.response?.data?.detail) || e?.message || "Upload failed");
    }
  };

  if (user === null) {
    return <CenterCard><Loader2 className="w-5 h-5 animate-spin text-brand-teal" /> Checking auth…</CenterCard>;
  }

  if (!isOwner) {
    return (
      <CenterCard>
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
        <div className="space-y-1">
          <div className="text-sm font-semibold text-brand-ink">Owner account required</div>
          <p className="text-xs text-brand-muted leading-relaxed">
            This route is reserved for the ColourMatch owner account. Sign in as the owner to use the quick-test flow.
          </p>
        </div>
      </CenterCard>
    );
  }

  if (status === "provisioning") {
    return (
      <CenterCard>
        <Wand2 className="w-5 h-5 text-brand-teal animate-pulse shrink-0" />
        <div className="space-y-1">
          <div className="text-sm font-semibold text-brand-ink">Spinning up your test project…</div>
          <p className="text-xs text-brand-muted leading-relaxed">Almost there — redirecting to the workspace.</p>
        </div>
      </CenterCard>
    );
  }

  if (status === "error") {
    return (
      <CenterCard>
        <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
        <div className="space-y-2">
          <div className="text-sm font-semibold text-brand-ink">Couldn&apos;t start the test project</div>
          <p className="text-xs text-brand-muted leading-relaxed">{error}</p>
          <button
            type="button"
            onClick={() => { setError(null); setStatus("choose"); }}
            data-testid="owner-quick-test-retry"
            className="mt-1 px-3 h-8 rounded-lg bg-brand-navy text-white text-xs font-semibold hover:bg-brand-navy/90"
          >
            Try again
          </button>
        </div>
      </CenterCard>
    );
  }

  // status === "choose"
  return (
    <div className="max-w-2xl mx-auto mt-12 px-5">
      <div
        data-testid="owner-quick-test-card"
        className="bg-white rounded-2xl shadow-soft border border-brand-border p-6 space-y-5"
      >
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-brand-teal flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Owner Quick-Test
          </div>
          <h1 className="text-xl font-bold tracking-tight text-brand-ink mt-1">Spin up a fresh test workspace</h1>
          <p className="text-sm text-brand-muted mt-1 leading-relaxed">
            Provisions an unlocked project tagged for QA. The render-feedback chip will appear under each render so you can log prompt quality during Day 2 testing.
          </p>
        </div>

        <div className="flex gap-2 p-1 bg-brand-sand/60 rounded-xl" data-testid="qt-scope-toggle">
          {[
            { v: "interior", l: "Interior" },
            { v: "exterior", l: "Exterior" },
          ].map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setScope(s.v)}
              data-testid={`qt-scope-${s.v}`}
              className={`flex-1 h-9 rounded-lg text-xs font-semibold transition-all ${
                scope === s.v ? "bg-white text-brand-ink shadow-sm" : "text-brand-muted hover:text-brand-ink"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            data-testid="qt-camera-btn"
            className="text-left rounded-xl border-2 border-brand-teal bg-brand-teal/10 hover:bg-brand-teal/15 p-4 transition-all"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Camera className="w-4 h-4 text-brand-teal" />
              <div className="text-sm font-bold text-brand-ink">Take live photo</div>
              <span className="text-[9px] font-bold tracking-widest uppercase bg-brand-navy text-white px-1.5 py-0.5 rounded-full ml-auto">Day 2</span>
            </div>
            <p className="text-xs text-brand-muted leading-relaxed">
              Opens your phone&apos;s rear camera directly. Best for in-the-room QA capture.
            </p>
          </button>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="qt-upload-btn"
            className="text-left rounded-xl border border-brand-border bg-white hover:border-brand-teal p-4 transition-all"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <ImagePlus className="w-4 h-4 text-brand-teal" />
              <div className="text-sm font-bold text-brand-ink">Pick from gallery</div>
            </div>
            <p className="text-xs text-brand-muted leading-relaxed">
              Real interior photo from your camera roll or computer.
            </p>
          </button>

          <button
            type="button"
            onClick={provisionSample}
            data-testid="qt-sample-btn"
            className="text-left rounded-xl border border-brand-border bg-white hover:border-brand-teal p-4 transition-all"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Wand2 className="w-4 h-4 text-brand-muted" />
              <div className="text-sm font-bold text-brand-ink">Use sample photo</div>
            </div>
            <p className="text-xs text-brand-muted leading-relaxed">
              Fast synthetic sample. Not representative of real Gemini quality.
            </p>
          </button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          data-testid="qt-file-input"
          className="hidden"
          onChange={(e) => provisionWithUpload(e.target.files?.[0])}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          data-testid="qt-camera-input"
          className="hidden"
          onChange={(e) => provisionWithUpload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="max-w-md mx-auto mt-16 px-5">
      <div
        data-testid="owner-quick-test-card"
        className="bg-white rounded-2xl shadow-soft border border-brand-border p-6 flex items-start gap-3"
      >
        {children}
      </div>
    </div>
  );
}
